import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { Constantes } from '../utilerias/constantes/constantes';
import { NotificacionUsuario } from '../shared/model/incidencias/notificacionUsuario.model';
import { TicketsService } from '../tickets/tickets.service';
import { UtileriasService } from './../utilerias/service/utilerias.service';
import { TipoNotificacionEnum } from '../utilerias/enums/tipoNotificacion.enum';
import { ComunicacionService } from '../shared/services/comunicacion.service';

@Component({
  selector: 'app-notificaciones',
  standalone: true,
  templateUrl: 'notificaciones.component.html',
  imports: [
    CommonModule,
    FormsModule,
    NgIf,
    NgFor,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOption,
    MatButtonModule,
  ],
})
export class NotificacionesComponent implements OnInit {
  notificaciones: NotificacionUsuario[] = [];
  totalRegistros = 0;
  totalPages = 0;
  currentPage = 1;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  loading = false;

  mensaje = '';
  tipo: TipoNotificacionEnum | null = null;
  tiposKeys = Object.keys(TipoNotificacionEnum);

  order = 'id';
  length = Constantes.CERO;
  pageIndex = Constantes.CERO;

  constructor(
    private service: TicketsService,
    private utilerias: UtileriasService,
    private comunicacion: ComunicacionService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.limpiarFiltros();
    this.iniciarPaginado();
    this.consultaPagina(1);
  }

  marcarVisto(idNotificacion: string): void {
    if (!idNotificacion) {
      return;
    }

    void firstValueFrom(this.service.cambiarBanderaTicket({ id: idNotificacion }))
      .then(() => {
        this.comunicacion.obtenerrNotificaciones();
        this.consultaPagina(this.currentPage);
      })
      .catch(() => {
        this.cdr.detectChanges();
      });
  }

  onMensajeChange(value: string): void {
    this.mensaje = value;
    this.consultaPagina(1);
  }

  onTipoChange(value: TipoNotificacionEnum | null): void {
    this.tipo = value;
    this.consultaPagina(1);
  }

  limpiarBusqueda(): void {
    this.limpiarFiltros();
    this.iniciarPaginado();
    this.consultaPagina(1);
  }

  consultaPagina(page: number): void {
    this.currentPage = page;
    this.pageIndex = page - 1;
    this.loading = this.notificaciones.length === 0;
    this.cdr.detectChanges();

    const inicio = this.pageIndex * this.pageSize;
    const numPg = this.utilerias.obtenerPagina(inicio, this.pageSize, this.length);
    const filtro = `${this.mensaje || ''}¬${this.tipo || ''}`;

    void firstValueFrom(
      this.service.obtenerCollecionPorPagina(numPg, this.pageSize, this.order, filtro)
    )
      .then((response) => {
        if (this.utilerias.validarObjecto(response)) {
          this.notificaciones = [...(response.content ?? [])];
          this.length = Number(response.totalElements ?? 0);
          this.totalRegistros = this.length;
        } else {
          this.notificaciones = [];
          this.length = 0;
          this.totalRegistros = 0;
        }

        this.totalPages =
          this.totalRegistros > 0 ? Math.ceil(this.totalRegistros / this.pageSize) : 0;

        if (this.totalPages > 0 && this.currentPage > this.totalPages) {
          this.consultaPagina(this.totalPages);
          return;
        }

        this.cdr.detectChanges();
      })
      .catch(() => {
        this.notificaciones = [];
        this.length = 0;
        this.totalRegistros = 0;
        this.totalPages = 0;
        this.cdr.detectChanges();
      })
      .finally(() => {
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.iniciarPaginado();
    this.consultaPagina(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultaPagina(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getStatusLabel(notificacion: NotificacionUsuario): string {
    return notificacion?.revisado ? 'Vista' : 'Pendiente';
  }

  getStatusClass(notificacion: NotificacionUsuario): string {
    return notificacion?.revisado ? 'is-active' : 'is-pending';
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }

  private limpiarFiltros(): void {
    this.mensaje = '';
    this.tipo = null;
  }

  private iniciarPaginado(): void {
    this.length = Constantes.CERO;
    this.pageIndex = Constantes.CERO;
  }
}
